import { AxiosResponse } from 'axios';
import { SettingResponse } from 'app/modules/settings/infrastructure/dto/response/SettingResponse';
import { AxiosInstance } from 'app/shared/infrastructure/api/web-service/axios-instance';
import { getToken } from 'app/shared/helpers/config';
import { PutSettingItemRequest } from 'app/modules/settings/infrastructure/dto/request/PutSettingItemRequest';
import { PatchShipmentAppointmentRequest } from 'app/modules/settings/infrastructure/dto/request/PatchShipmentAppointmentRequest';
import { PatchPickHandleRequest } from 'app/modules/settings/infrastructure/dto/request/PatchPickHandleRequest';
import { PatchSampleAccountRequest } from 'app/modules/settings/infrastructure/dto/request/PatchSampleAccountRequest';
import { PatchSampleDiscountRequest } from 'app/modules/settings/infrastructure/dto/request/PatchSampleDiscountRequest';
import { BannerPageResponse } from 'app/modules/settings/infrastructure/dto/response/BannerPageResponse';
import { PutBannerItemRequest } from 'app/modules/settings/infrastructure/dto/request/PutBannerItemRequest';
import { WarehouseContactResponse } from 'app/modules/settings/infrastructure/dto/response/WarehouseContactResponse';
import { PatchWarehouseContactRequest } from 'app/modules/settings/infrastructure/dto/request/PatchWarehouseContactRequest';
import { MaintenancePageResponse } from 'app/modules/settings/infrastructure/dto/response/MaintenancePageResponse';
import { PutMaintenanceItemRequest } from 'app/modules/settings/infrastructure/dto/request/PutMaintenanceItemRequest';
import { PatchInMaintenanceRequest } from 'app/modules/settings/infrastructure/dto/request/PatchInMaintenanceRequest';
import { ShipmentLeadTimeResponse } from 'app/modules/settings/infrastructure/dto/response/ShipmentLeadTimeResponse';
import { UpdateShipmentLeadTimeRequest } from 'app/modules/settings/infrastructure/dto/request/UpdateShipmentLeadTimeRequest';
import { ProductSyncStatusResponse } from 'app/modules/settings/infrastructure/dto/response/ProductSyncStatusResponse';
import { SpecificSyncStatusListResponse } from 'app/modules/settings/infrastructure/dto/response/SpecificSyncStatusResponse';

export interface TencerAdminApiClient {
    getSettings: () => Promise<AxiosResponse<SettingResponse[]>>
    putSettingItem: (request: PutSettingItemRequest) => Promise<AxiosResponse<string>>
    patchShipmentAppointment: (request: PatchShipmentAppointmentRequest) => Promise<AxiosResponse<void>>
    getShipmentLeadTimes: () => Promise<AxiosResponse<ShipmentLeadTimeResponse[]>>
    updateShipmentLeadTime: (id: string, request: { lead_days: number }) => Promise<AxiosResponse<void>>
    getSampleAccount: () => Promise<AxiosResponse<SettingResponse>>
    patchSampleAccount: (request: PatchSampleAccountRequest) => Promise<AxiosResponse<void>>
    getSampleQuantityLimitDiscountPercent: () => Promise<AxiosResponse<SettingResponse[]>>
    patchSampleQuantityLimitDiscountPercent: (request: PatchSampleDiscountRequest) => Promise<AxiosResponse<void>>
}

export class AxiosTencerAdminApiClient implements TencerAdminApiClient {

    public constructor() {
    }

    public async getSettings(): Promise<AxiosResponse<SettingResponse[]>> {
        const endpoint = '/api/admin/settings/';
        return AxiosInstance.get<undefined,
        AxiosResponse <SettingResponse[]>>(endpoint, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
            },
        });
    }

    public async getWarehouseContacts(): Promise<AxiosResponse<WarehouseContactResponse[]>> {
        const endpoint = '/api/admin/settings/warehouse-contacts';
        return AxiosInstance.get<undefined,
        AxiosResponse <WarehouseContactResponse[]>>(endpoint, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
            },
        });
    }

    public async putSettingItem(request: PutSettingItemRequest): Promise<AxiosResponse<string>> {
        const endpoint = '/api/admin/settings/';
        return AxiosInstance.put<undefined,
        AxiosResponse <string>>(endpoint, request, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
            },
        });
    }

    public async patchShipmentAppointment(request: PatchShipmentAppointmentRequest): Promise<AxiosResponse<void>> {
        const endpoint = '/api/admin/settings/shipment-appointment';
        return AxiosInstance.patch(endpoint, request, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
            },
        });
    }

    public async patchPickHandle(request: PatchPickHandleRequest): Promise<AxiosResponse<void>> {
        const endpoint = '/api/admin/settings/pick-handle';
        return AxiosInstance.patch(endpoint, request, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
            },
        });
    }

    public async patchWarehouseContact(request: PatchWarehouseContactRequest): Promise<AxiosResponse<void>> {
        const endpoint = `/api/admin/settings/warehouse-contacts/${request.code}`;
        return AxiosInstance.patch(endpoint, request.payload, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
            },
        });
    }

    public async fetchGetBannersPages(): Promise<AxiosResponse<BannerPageResponse[]>> {
        const endpoint = '/api/admin/banners/';
        return AxiosInstance.get<undefined,
        AxiosResponse <BannerPageResponse[]>>(endpoint, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
            },
        });
    }

    public async fetchPutBannerItem(request: PutBannerItemRequest): Promise<AxiosResponse<string>> {
        const endpoint = `/api/admin/banners/${request.bannerId}`;
        return AxiosInstance.put<undefined,
        AxiosResponse <string>>(endpoint, request.payload, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
            },
        });
    }

    public async fetchGetMaintenanceByOrigin(origin: number): Promise<AxiosResponse<MaintenancePageResponse>> {
        const endpoint = `/api/admin/maintenance-messages/${origin}`;
        return AxiosInstance.get<undefined,
        AxiosResponse <MaintenancePageResponse>>(endpoint, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
            },
        });
    }

    public async fetchPutMaintenanceItem(request: PutMaintenanceItemRequest): Promise<AxiosResponse<string>> {
        const endpoint = `/api/admin/maintenance-messages/${request.maintenanceId}`;
        return AxiosInstance.put<undefined,
        AxiosResponse <string>>(endpoint, request.payload, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
            },
        });
    }

    public async fetchGetInMaintenanceSettings(): Promise<AxiosResponse<SettingResponse[]>> {
        const endpoint = '/api/admin/settings/in-maintenance';
        return AxiosInstance.get<undefined,
        AxiosResponse <SettingResponse[]>>(endpoint, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
            },
        });
    }

    public async fetchPatchInMaintenance(request: PatchInMaintenanceRequest): Promise<AxiosResponse<void>> {
        const endpoint = '/api/admin/settings/in_maintenance';
        return AxiosInstance.patch(endpoint, request, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
            },
        });
    }

    public async getShipmentLeadTimes(): Promise<AxiosResponse<ShipmentLeadTimeResponse[]>> {
        const endpoint = '/api/admin/shipment-lead-times';
        return AxiosInstance.get<undefined, AxiosResponse<ShipmentLeadTimeResponse[]>>(
            endpoint,
            { headers: { Authorization: `Bearer ${getToken()}` } },
        );
    }

    public async updateShipmentLeadTime(
        id: string,
        request: UpdateShipmentLeadTimeRequest,
    ): Promise<AxiosResponse<void>> {
        const endpoint = `/api/admin/shipment-lead-times/${id}`;
        return AxiosInstance.put<undefined, AxiosResponse<void>>(
            endpoint,
            request,
            { headers: { Authorization: `Bearer ${getToken()}` } },
        );
    }

    public async fetchGetProductSyncStatus(command: string): Promise<AxiosResponse<ProductSyncStatusResponse[]>> {
        const endpoint = `/api/admin/synchronization/${command}`;
        return AxiosInstance.get<undefined,
        AxiosResponse<ProductSyncStatusResponse[]>>(endpoint, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
            },
        });
    }

    public async fetchPostProductSync(command: string): Promise<AxiosResponse<void>> {
        const endpoint = `/api/admin/synchronization/command/${command}`;
        return AxiosInstance.post(endpoint, {}, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
            },
        });
    }

    public async fetchGetSpecificSyncStatus(
        limit: number,
        offset: number,
    ): Promise<AxiosResponse<SpecificSyncStatusListResponse>> {
        const endpoint = `/api/admin/synchronization/specific?limit=${limit}&offset=${offset}`;
        return AxiosInstance.get<undefined,
        AxiosResponse<SpecificSyncStatusListResponse>>(endpoint, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
            },
        });
    }

    public async fetchPostSyncProduct(productId: string): Promise<AxiosResponse<void>> {
        const endpoint = `/api/admin/synchronization/product/${productId}`;
        return AxiosInstance.post(endpoint, {}, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
            },
        });
    }

    public async fetchPostSyncErpCode(erpCode: string): Promise<AxiosResponse<void>> {
        const endpoint = `/api/admin/synchronization/erp-code/${erpCode}`;
        return AxiosInstance.post(endpoint, {}, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
            },
        });
    }

    public async getSampleAccount(): Promise<AxiosResponse<SettingResponse>> {
        const endpoint = '/api/admin/settings/sample-account';
        return AxiosInstance.get<undefined, AxiosResponse<SettingResponse>>(endpoint, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
            },
        });
    }

    public async patchSampleAccount(request: PatchSampleAccountRequest): Promise<AxiosResponse<void>> {
        const endpoint = '/api/admin/settings/sample-account';
        return AxiosInstance.patch(endpoint, request, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
            },
        });
    }

    public async getSampleQuantityLimitDiscountPercent(): Promise<AxiosResponse<SettingResponse[]>> {
        const endpoint = '/api/admin/settings/sample-quantity-limit-discount-percent';
        return AxiosInstance.get<undefined, AxiosResponse<SettingResponse[]>>(endpoint, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
            },
        });
    }

    public async patchSampleQuantityLimitDiscountPercent(
        request: PatchSampleDiscountRequest,
    ): Promise<AxiosResponse<void>> {
        const endpoint = '/api/admin/settings/sample-quantity-limit-discount-percent';
        return AxiosInstance.patch(endpoint, request, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
            },
        });
    }

}
